import { useIntersectionObserver } from "@uidotdev/usehooks";
import { cn } from "cn";
import { isEmpty } from "lodash-es";
import { AnimatePresence, motion } from "motion/react";
import pluralize from "pluralize";
import { useEffect, useMemo, useRef, useState } from "react";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";
import { useOpenSnapshot } from "@/hooks/use-open-snapshot";

import {
  ActiveFilterChip,
  EMPTY_VALUE_LABEL,
} from "../_components/active-filter-chip";
import { FilterValueListSkeleton } from "../_components/filter-value-list-skeleton";
import { useFilters } from "../_hooks/use-filters";
import type { FilterField, FilterOption } from "../types";

/**
 * Async, searchable MULTI-select for large/remote option sets (users, places,
 * accounts). The field's `useOptions({ selectedValues })` owns the search query,
 * paging, AND the resolved `selectedOptions` (so the chip can show pills/labels
 * for ids that aren't on the current search page).
 *
 * The chip value renders overlapping `pill` (or `icon`) visuals + a count; the
 * option list shows each option's `icon` and groups the current selection first
 * — matching the office assignee/place chips. The value is a `string[]` of ids
 * folding to `{ [key]: { in } }`.
 */
export const MultiEntityFilter = ({ field }: { field: FilterField }) => {
  const { filters, modify, clear } = useFilters();
  const raw = (filters as Record<string, unknown>)[field.key];
  const selectedValues = Array.isArray(raw) ? (raw as string[]) : [];
  // multiEntity fields always declare useOptions
  const { options, selectedOptions, isLoading, onSearch, hasMore, fetchMore } =
    field.useOptions!({ selectedValues });
  const resolvedSelected: FilterOption[] = selectedValues.length
    ? (selectedOptions ?? [])
    : [];
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(() => selectedValues.length === 0);

  const [ref, entry] = useIntersectionObserver({
    threshold: 0.5,
    rootMargin: "100px",
  });

  useEffect(() => {
    if (entry?.isIntersecting && hasMore) fetchMore?.();
  }, [entry?.isIntersecting, hasMore, fetchMore]);

  const handleSearch = (query: string) => {
    setSearch(query);
    onSearch?.(query);
  };

  const toggle = (optionValue: string) => {
    const next = selectedValues.includes(optionValue)
      ? selectedValues.filter((item) => item !== optionValue)
      : [...selectedValues, optionValue];
    modify({ [field.key]: next });
  };

  const renderOption = (option: FilterOption) => {
    const isChecked = selectedValues.includes(option.value);
    return (
      <CommandItem
        key={option.value}
        value={option.value}
        onSelect={() => toggle(option.value)}
        className="group text-text-dark-high flex gap-2 items-center"
      >
        <Checkbox
          checked={isChecked}
          className={cn(
            !isChecked && "opacity-0 group-data-[selected=true]:opacity-100",
          )}
        />
        {option.icon}
        {option.detail ?? (
          <span className="text-sm leading-5 font-normal text-text-dark-max line-clamp-1 break-all">
            {option.label}
          </span>
        )}
      </CommandItem>
    );
  };

  const valueLabel =
    selectedValues.length === 0 ? (
      EMPTY_VALUE_LABEL
    ) : (
      <div className="flex gap-1 items-center">
        {resolvedSelected.length > 0 && (
          <div className="flex items-center flex-row -space-x-1.5">
            <AnimatePresence mode="popLayout">
              {resolvedSelected.slice(0, 3).map((option) => (
                <motion.div
                  key={option.value}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -10 }}
                  transition={{ duration: 0.2 }}
                >
                  {option.pill ?? option.icon}
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        )}
        <span className="text-xs leading-4 font-normal text-text-dark-high">
          {selectedValues.length === 1
            ? (resolvedSelected[0]?.label ?? EMPTY_VALUE_LABEL)
            : `${selectedValues.length} ${pluralize(field.label.toLowerCase(), selectedValues.length)}`}
        </span>
      </div>
    );

  // Every option object this picker has seen, by id. `selectedOptions` comes from a query keyed on
  // the LIVE selection, so an id dropped by a tick stops resolving — without this cache, a row
  // frozen into the top group would vanish mid-interaction. Append-only and idempotent, which is
  // what makes the render-phase write safe.
  const knownOptionsRef = useRef(new Map<string, FilterOption>());
  for (const option of [...options, ...(selectedOptions ?? [])]) {
    knownOptionsRef.current.set(option.value, option);
  }

  // Grouped by the ids the popover OPENED with — available synchronously, unlike the resolved
  // objects, so the split is frozen from the opening's first frame. Regrouping on each tick moved
  // the row across the separator, which remounts it, resets cmdk's highlight to the first row and
  // scrolls the list back to the top. Checkboxes stay live, so a ticked row fills in place, and a
  // row un-ticked while open stays put with an empty box until the popover is reopened.
  const openedSelected = useOpenSnapshot(selectedValues, { open });
  const openedIds = useMemo(() => new Set(openedSelected), [openedSelected]);

  const openedSelectedOptions = openedSelected
    .map((id) => knownOptionsRef.current.get(id))
    .filter((option): option is FilterOption => Boolean(option));
  // Excluded by the SNAPSHOT, not the live selection — that is what keeps a just-ticked row from
  // rendering in both groups at once (two rows sharing a cmdk value are both `aria-selected`).
  const unselectedOptions = options.filter(
    (option) => !openedIds.has(option.value),
  );

  return (
    <ActiveFilterChip
      field={field}
      valueLabel={valueLabel}
      selectedCount={selectedValues.length}
      open={open}
      onOpenChange={setOpen}
      onRemove={() => clear(field.key)}
    >
      <Command
        className={cn(
          /* sorted */
          "bg-bg-surface-elevate-low",
          field.popoverWidthClassName ?? "w-70",
        )}
        shouldFilter={false}
      >
        <CommandInput
          placeholder={`${field.label}...`}
          className="h-9"
          value={search}
          onValueChange={handleSearch}
        />
        <CommandList>
          {isLoading && options.length === 0 ? (
            <FilterValueListSkeleton />
          ) : (
            <div className="relative">
              <CommandEmpty>No results found.</CommandEmpty>
              {!isEmpty(openedSelectedOptions) && (
                <CommandGroup>
                  {openedSelectedOptions.map(renderOption)}
                </CommandGroup>
              )}
              {!isEmpty(openedSelectedOptions) &&
                !isEmpty(unselectedOptions) && <CommandSeparator />}
              {!isEmpty(unselectedOptions) && (
                <CommandGroup>
                  {unselectedOptions.map(renderOption)}
                  {hasMore && <FilterValueListSkeleton />}
                </CommandGroup>
              )}
              <div ref={ref} className="absolute bottom-30 left-0 right-0" />
            </div>
          )}
        </CommandList>
      </Command>
    </ActiveFilterChip>
  );
};
